import { FC } from "react";
import styled from "styled-components";
import { LayoutProps, layout, BackgroundColorProps } from "styled-system";
import css from "@styled-system/css";

const LoaderContainer = styled.section<LayoutProps>`
  position: relative;
  animation: rotate 1000ms linear infinite;

  &.loader {
    display: inline-flex;
  }

  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }

    to {
      transform: rotate(360deg);
    }
  }
  ${layout}
`;

const DotsContainer = styled.div<{
  angle: number;
}>`
  position: absolute;
  top: 40%;
  width: 100%;
  height: 20%;
  display: inline-flex;
  justify-content: space-between;
  ${(props) =>
    css({
      transform: `rotate(${props.angle}deg)`,
    })}
`;

const Dot = styled.span<{
  dotsColor: BackgroundColorProps["backgroundColor"];
}>`
  ${(props) =>
    css({
      width: "20%",
      height: "100%",
      borderRadius: "50%",
      backgroundColor: props.dotsColor as string,
    })}
`;

const DotsGroup = ({
  numberOfDots,
  dotsColor,
  angle,
}: {
  numberOfDots: 1 | 2;
  dotsColor: BackgroundColorProps["backgroundColor"];
  angle: number;
}) => {
  return (
    <DotsContainer angle={angle}>
      {[...Array(numberOfDots)].map((_, index) => (
        <Dot dotsColor={dotsColor} key={index}></Dot>
      ))}
    </DotsContainer>
  );
};

export interface CircularLoaderProps {
  title: string;
  /**
   *The element's unique identifier
   */
  id?: string;
  width?: LayoutProps["width"];
  height?: LayoutProps["height"];
  dotsColor?: BackgroundColorProps["backgroundColor"];
}

const CircularLoader: FC<CircularLoaderProps> = ({
  title,
  id,
  width = 100,
  height = 100,
  dotsColor = "primary-light-blue",
}) => {
  return (
    <LoaderContainer
      id={id}
      aria-label={title}
      width={width}
      height={height}
      role="alert"
      aria-live="assertive"
      className="loader"
    >
      <DotsGroup numberOfDots={2} dotsColor={dotsColor} angle={0} />
      <DotsGroup numberOfDots={2} dotsColor={dotsColor} angle={45} />
      <DotsGroup numberOfDots={2} dotsColor={dotsColor} angle={90} />
      <DotsGroup numberOfDots={1} dotsColor={dotsColor} angle={135} />
    </LoaderContainer>
  );
};

export default CircularLoader;
